import React, { useEffect, useState, useMemo, useContext } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import { SidebarContext } from '../context/SidebarContext';
import './Header.css';

const Layout = ({ children, loading = false }) => {
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  const [safeAreaBottom, setSafeAreaBottom] = useState(0);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const sidebarContext = useContext(SidebarContext);
  const sidebarWidth = sidebarContext?.sidebarWidth || 200;

  useEffect(() => {
    const checkMobileView = () => {
      const newIsMobileView = window.innerWidth < 768;
      setIsMobileView(newIsMobileView);
    };

    const updateSafeArea = () => {
      // 실제 CSS env() 값을 직접 읽기
      const testEl = document.createElement('div');
      testEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
      document.body.appendChild(testEl);
      const computedStyle = window.getComputedStyle(testEl);
      const actualSafeArea = parseInt(computedStyle.paddingBottom) || 0;
      document.body.removeChild(testEl);
      
      // iOS Safari의 동적 Safe Area 처리
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isIOS && isSafari) {
        // 실제 safe area 값이 15px 미만이면 0으로 처리 (주소창 보이는 상태)
        const dynamicSafeArea = actualSafeArea < 15 ? 0 : actualSafeArea;
        setSafeAreaBottom(dynamicSafeArea);
      } else {
        // 다른 브라우저는 실제 값 사용
        setSafeAreaBottom(actualSafeArea);
      }
    };

    const debouncedUpdate = () => {
      setTimeout(() => {
        checkMobileView();
        updateSafeArea();
      }, 100);
    };

    checkMobileView();
    updateSafeArea();
    
    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', debouncedUpdate);
    window.addEventListener('scroll', debouncedUpdate); // iOS Safari 주소창 토글 감지
    
    // visualViewport API 사용 (지원하는 경우)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedUpdate);
    }

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', debouncedUpdate);
      window.removeEventListener('scroll', debouncedUpdate);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', debouncedUpdate);
      }
    };
  }, []);

  // 컨텐츠 페이드인 효과 제거
  useEffect(() => {
    setIsContentVisible(true); // 즉시 표시
  }, [children]);

  // 메모화를 통해 불필요한 리렌더링 방지
  const layoutClasses = useMemo(() => ({
    main: `flex-1 bg-gray-100 ${isMobileView ? 'flex flex-col overflow-auto' : 'overflow-hidden'}`,
    container: "flex flex-col h-screen",
    contentWrapper: `flex-1 overflow-hidden transition-all duration-300` // 동적 패딩은 스타일로 처리, 트랜지션 추가
  }), [isMobileView]);

  // 모바일에서 메인 컨텐츠 스타일 - viewport 정확한 계산
  const mainStyle = useMemo(() => {
    if (!isMobileView) {
      // 데스크톱에서 통일된 레이아웃 적용
      return {
        padding: '1rem', // 16px - 모든 페이지 통일
        boxSizing: 'border-box',
        height: '100%',
        overflow: 'auto'
      };
    }
    
    // 높이 계산: 전체 viewport - 헤더(50px) 만 계산 (네비게이션바 높이 제외)
    const contentHeight = `calc(100vh - 50px)`;
    const contentHeightDynamic = `calc(100dvh - 50px)`;
    
    return {
      // 높이 설정
      height: contentHeight,
      height: contentHeightDynamic,
      maxHeight: contentHeight,
      maxHeight: contentHeightDynamic,
      minHeight: contentHeight,
      minHeight: contentHeightDynamic,
      
      // 스크롤 설정
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
      
      // 성능 최적화
      transform: 'translateZ(0)',
      willChange: 'scroll-position',
      
      // 학습 분석 기준 모바일 여백 적용
      padding: '1rem', // 16px - 학습분석 기준
      paddingTop: '1rem',
      // 네비게이션바 뒤로 컨텐츠가 지나가도록 하단 패딩 추가
      paddingBottom: `calc(1rem + 68px + ${safeAreaBottom}px)`,
      
      // 위치 고정
      position: 'relative',
      
      // 박스 사이징
      boxSizing: 'border-box',
    };
  }, [isMobileView, safeAreaBottom]);

  // 모바일 컨테이너 스타일
  const containerStyle = useMemo(() => {
    if (!isMobileView) return {};
    
    return {
      height: '100vh',
      height: '100dvh',
      maxHeight: '100vh',
      maxHeight: '100dvh',
      position: 'relative',
    };
  }, [isMobileView]);

  return (
    <div className={layoutClasses.container} style={containerStyle}>
      {/* 모든 디바이스에서 헤더 표시 */}
      <Header />
      {/* 헤더 공간 확보 */}
      <div style={{ height: '50px' }} />
      <div className={layoutClasses.contentWrapper}>
        <main className={layoutClasses.main} style={mainStyle}>
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="size-12 animate-spin rounded-full border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">로딩 중...</p>
              </div>
            </div>
          ) : (
            typeof children === 'function' ? children({ isMobileView, safeAreaBottom }) : children
          )}
        </main>
      </div>
      
      {/* 모바일 하단 네비게이션바는 App.js에서 단일 인스턴스로 렌더링 */}
    </div>
  );
};

export default React.memo(Layout); 